import {Button, FormSectionHeading, Icon, Typography} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {FatcaCrsBeneficiaryTable} from './FatcaCrsBeneficiaryTable';

import type {FatcaCrsBeneficiarySnapshotDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface SupplementaryBeneficiaryTablesProps {
  /** Beneficiaries being added now — always-visible table. */
  addedBeneficiaries: FatcaCrsBeneficiarySnapshotDTO[];
  /** Existing roster (sellers + previous beneficiaries) — shown behind the expand toggle. */
  existingBeneficiaries: FatcaCrsBeneficiarySnapshotDTO[];
}

const PREFIX = 'activation.packages.compliance.supplementary';

export const SupplementaryBeneficiaryTables: FC<SupplementaryBeneficiaryTablesProps> = ({
  addedBeneficiaries,
  existingBeneficiaries
}) => {
  const {t} = useTranslation('trustAccounts');
  const [rosterExpanded, setRosterExpanded] = useState(false);

  return (
    <div className="flex flex-col gap-6" data-slot="supplementary-beneficiary-tables">
      {/* Added beneficiaries — always visible */}
      <div className="flex flex-col gap-3">
        <FormSectionHeading
          title={t(`${PREFIX}.addedBeneficiaries.title`)}
          description={t(`${PREFIX}.addedBeneficiaries.supportingText`)}
        />
        {addedBeneficiaries.length > 0 ? (
          <FatcaCrsBeneficiaryTable rows={addedBeneficiaries} />
        ) : (
          <Typography size="sm" color="tertiary">
            {t(`${PREFIX}.addedBeneficiaries.empty`)}
          </Typography>
        )}
      </div>

      {/* Full roster — collapsible */}
      {existingBeneficiaries.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <FormSectionHeading
              title={t(`${PREFIX}.fullRoster.title`)}
              description={t(`${PREFIX}.fullRoster.supportingText`)}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0"
              icon={
                <Icon
                  name="chevron-down"
                  className={cn('transition-transform', rosterExpanded && 'rotate-180')}
                />
              }
              iconPosition="end"
              onClick={() => setRosterExpanded(prev => !prev)}
            >
              {t(`${PREFIX}.fullRoster.expand`)}
            </Button>
          </div>
          {rosterExpanded && <FatcaCrsBeneficiaryTable rows={existingBeneficiaries} />}
        </div>
      )}
    </div>
  );
};
